import { type Anchor, type Rect } from '@st/geom'
import { Context, Effect, Stream } from 'effect'
import type { PageRect, ViewerViewportState } from './viewer-geometry'

const POINTS_PER_INCH = 72
const STANDARD_SCREEN_PX_PER_INCH = 96

export const DEFAULT_CSS_PX_PER_PDF_PT = STANDARD_SCREEN_PX_PER_INCH / POINTS_PER_INCH

export function resolveCssPxPerPdfPt(_devicePixelRatio: number) {
  return DEFAULT_CSS_PX_PER_PDF_PT
}

export type ViewportMeasurement = {
  containerWidthPx: number
  containerHeightPx: number
  cssPxPerPdfPt: number
}

export type ViewportScrollTarget = {
  target: Rect
  viewportAlignment: Anchor
  targetAlignment: Anchor
}

export class ViewportService extends Context.Service<
  ViewportService,
  {
    prepareForDocumentOpen: () => Effect.Effect<void>
    bind: (containerId: string | null) => Effect.Effect<void>
    // Waits until the bound viewport can issue the request, then resolves after
    // the viewport has actually landed at the requested target. It keeps
    // re-issuing until then; the caller interrupts it when the user takes over.
    scrollToTarget: (target: ViewportScrollTarget, behavior?: ScrollBehavior) => Effect.Effect<void>
    pinViewportCenterAfterNextFrame: () => Effect.Effect<void>
    /**
     * One element per user scroll input on the bound viewport (wheel, touch,
     * scroll keys, a press on its scrollbar); programmatic scrolling emits
     * nothing. Observation lifetime belongs to the consuming state-owned Task.
     */
    userScrolls: Stream.Stream<void>
    /** Observation lifetime belongs to the consuming state-owned Task. */
    measurements: Stream.Stream<ViewportMeasurement>
    /** Caller provides page rects; service handles scroll → viewport state. */
    viewportStates: (pageRects: PageRect[]) => Stream.Stream<ViewerViewportState>
  }
>()('ViewportService') {}
